import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, HandshakeIcon, LogInIcon, UserIcon, ChevronDownIcon, PackageIcon, CalendarIcon } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
export function Navbar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isGetStartedOpen, setIsGetStartedOpen] = useState(false);
  const {
    currentUser
  } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const getDashboardLink = () => {
    if (!currentUser) return '/login';
    switch (currentUser.type) {
      case 'brand':
        return '/dashboard/brand';
      case 'organizer':
        return '/dashboard/organizer';
      case 'admin':
      case 'super_admin':
        return '/admin';
      default:
        return '/login';
    }
  };
  const isActive = (path: string) => location.pathname === path;
  const scrollToFeatures = (e: React.MouseEvent) => {
    e.preventDefault();
    if (location.pathname !== '/') {
      navigate('/#features');
    } else {
      document.getElementById('features')?.scrollIntoView({
        behavior: 'smooth'
      });
    }
    setIsMenuOpen(false);
  };
  return <nav className="bg-white/95 backdrop-blur-md shadow-sm border-b border-gray-100 sticky top-0 z-50" data-id="element-609">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-610">
        <div className="flex justify-between items-center h-16" data-id="element-611">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 group" data-id="element-612">
            <div className="bg-blue-600 p-2 rounded-lg shadow-sm group-hover:shadow-md transition-shadow" data-id="element-613">
              <HandshakeIcon className="h-5 w-5 text-white" data-id="element-614" />
            </div>
            <span className="text-xl font-bold text-gray-900" data-id="element-615">SponsrAI</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1" data-id="element-616">
            <a href="#features" onClick={scrollToFeatures} className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-blue-600 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-617">
              How It Works
            </a>
            <Link to="/brands" className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${isActive('/brands') ? 'text-blue-600 bg-blue-50' : 'text-gray-600 hover:text-blue-600 hover:bg-gray-50'}`} data-id="element-618">
              Browse Brands
            </Link>
            <Link to="/organizers" className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${isActive('/organizers') ? 'text-blue-600 bg-blue-50' : 'text-gray-600 hover:text-blue-600 hover:bg-gray-50'}`} data-id="element-619">
              Browse Organizers
            </Link>

            <div className="w-px h-6 bg-gray-200 mx-2" data-id="element-620" />

            {/* Get Started Dropdown */}
            {!currentUser && <div className="relative" onMouseEnter={() => setIsGetStartedOpen(true)} onMouseLeave={() => setIsGetStartedOpen(false)} data-id="element-621">
                <button className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 hover:text-blue-600 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-622">
                  Get Started
                  <ChevronDownIcon className={`ml-1 h-4 w-4 transition-transform ${isGetStartedOpen ? 'rotate-180' : ''}`} data-id="element-623" />
                </button>

                {isGetStartedOpen && <div className="absolute top-full right-0 mt-1 w-64 bg-white rounded-xl shadow-lg border border-gray-100 py-2 z-50" data-id="element-624">
                    <Link to="/onboarding/brand" className="flex items-center px-4 py-3 hover:bg-gray-50 transition-colors" data-id="element-625">
                      <div className="bg-blue-100 p-2 rounded-lg mr-3" data-id="element-626">
                        <PackageIcon className="h-5 w-5 text-blue-600" data-id="element-627" />
                      </div>
                      <div data-id="element-628">
                        <div className="font-medium text-gray-900" data-id="element-629">
                          I'm a Brand
                        </div>
                        <div className="text-xs text-gray-500" data-id="element-630">
                          Find event partnerships
                        </div>
                      </div>
                    </Link>
                    <Link to="/onboarding/organizer" className="flex items-center px-4 py-3 hover:bg-gray-50 transition-colors" data-id="element-631">
                      <div className="bg-purple-100 p-2 rounded-lg mr-3" data-id="element-632">
                        <CalendarIcon className="h-5 w-5 text-purple-600" data-id="element-633" />
                      </div>
                      <div data-id="element-634">
                        <div className="font-medium text-gray-900" data-id="element-635">
                          I'm an Organizer
                        </div>
                        <div className="text-xs text-gray-500" data-id="element-636">
                          Find brand sponsors
                        </div>
                      </div>
                    </Link>
                  </div>}
              </div>}

            <Link to={getDashboardLink()} className="flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors shadow-sm hover:shadow" data-id="element-637">
              {currentUser ? <>
                  <UserIcon className="h-4 w-4 mr-1.5" data-id="element-638" />
                  Dashboard
                </> : <>
                  <LogInIcon className="h-4 w-4 mr-1.5" data-id="element-639" />
                  Login
                </>}
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="md:hidden p-2 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors" data-id="element-640">
            {isMenuOpen ? <X className="h-6 w-6" data-id="element-641" /> : <Menu className="h-6 w-6" data-id="element-642" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && <div className="md:hidden bg-white border-t border-gray-100" data-id="element-643">
          <div className="px-4 py-4 space-y-2" data-id="element-644">
            <a href="#features" onClick={scrollToFeatures} className="block px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg font-medium" data-id="element-645">
              How It Works
            </a>
            <Link to="/brands" className={`block px-4 py-3 rounded-lg font-medium ${isActive('/brands') ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-50'}`} onClick={() => setIsMenuOpen(false)} data-id="element-646">
              Browse Brands
            </Link>
            <Link to="/organizers" className={`block px-4 py-3 rounded-lg font-medium ${isActive('/organizers') ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-50'}`} onClick={() => setIsMenuOpen(false)} data-id="element-647">
              Browse Organizers
            </Link>

            <div className="border-t border-gray-100 pt-4 mt-4" data-id="element-648">
              <p className="px-4 text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2" data-id="element-649">
                Get Started
              </p>
              <Link to="/onboarding/brand" className="flex items-center px-4 py-3 hover:bg-gray-50 rounded-lg" onClick={() => setIsMenuOpen(false)} data-id="element-650">
                <div className="bg-blue-100 p-2 rounded-lg mr-3" data-id="element-651">
                  <PackageIcon className="h-5 w-5 text-blue-600" data-id="element-652" />
                </div>
                <div data-id="element-653">
                  <div className="font-medium text-gray-900" data-id="element-654">I'm a Brand</div>
                  <div className="text-xs text-gray-500" data-id="element-655">
                    Find event partnerships
                  </div>
                </div>
              </Link>
              <Link to="/onboarding/organizer" className="flex items-center px-4 py-3 hover:bg-gray-50 rounded-lg" onClick={() => setIsMenuOpen(false)} data-id="element-656">
                <div className="bg-purple-100 p-2 rounded-lg mr-3" data-id="element-657">
                  <CalendarIcon className="h-5 w-5 text-purple-600" data-id="element-658" />
                </div>
                <div data-id="element-659">
                  <div className="font-medium text-gray-900" data-id="element-660">
                    I'm an Organizer
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-661">
                    Find brand sponsors
                  </div>
                </div>
              </Link>
            </div>

            <div className="pt-4" data-id="element-662">
              <Link to={getDashboardLink()} className="flex items-center justify-center w-full px-4 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors" onClick={() => setIsMenuOpen(false)} data-id="element-663">
                {currentUser ? <>
                    <UserIcon className="h-4 w-4 mr-2" data-id="element-664" />
                    Dashboard
                  </> : <>
                    <LogInIcon className="h-4 w-4 mr-2" data-id="element-665" />
                    Login
                  </>}
              </Link>
            </div>
          </div>
        </div>}
    </nav>;
}